import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { ArrowDown, Sparkle, Play, X } from "@phosphor-icons/react";
import { IMAGES, API } from "@/lib/site";

const toEmbed = (url) => {
  if (!url) return null;
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|shorts\/))([\w-]{11})/);
  if (yt) return { type: "iframe", src: `https://www.youtube.com/embed/${yt[1]}?autoplay=1` };
  const vim = url.match(/vimeo\.com\/(\d+)/);
  if (vim) return { type: "iframe", src: `https://player.vimeo.com/video/${vim[1]}?autoplay=1` };
  return { type: "video", src: url };
};

const heroTitle = {
  hidden: { opacity: 0, y: 26 },
  show: { opacity: 1, y: 0, transition: { duration: 1, delay: 0.3, ease: [0.16, 1, 0.3, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const [videoUrl, setVideoUrl] = useState("");
  const [showVideo, setShowVideo] = useState(false);

  useEffect(() => {
    fetch(`${API}/settings/hero`)
      .then((r) => r.json())
      .then((d) => setVideoUrl(d.url || ""))
      .catch(() => {});
  }, []);

  const embed = toEmbed(videoUrl);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const yTex = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const scaleImg = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  const scrollDown = () =>
    document.querySelector("#pillars")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="top" ref={ref} className="relative min-h-screen overflow-hidden grain">
      <motion.div
        style={{ y: yTex, backgroundImage: `url(${IMAGES.texture})` }}
        className="absolute inset-0 bg-cover bg-center opacity-[0.12]"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-brand-bg/60 via-brand-bg/40 to-brand-bg" />

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-36 lg:pt-44 pb-20 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex items-center gap-3 mb-8"
          >
            <Sparkle size={16} weight="fill" className="text-brand-gold" />
            <span className="overline">TonyBrand29 · Doctor & Coach</span>
          </motion.div>

          <motion.h1
            variants={heroTitle}
            initial="hidden"
            animate="show"
            data-testid="hero-heading"
            className="font-serif font-light leading-[1.04] text-[9vw] sm:text-6xl lg:text-[4rem] text-brand-text"
          >
            Your doctor for health, wealth mindset and{" "}
            <span className="italic text-brand-gold">progressively productive relationship.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.1 }}
            className="mt-8 max-w-xl text-brand-dim text-base sm:text-lg leading-relaxed"
          >
            Evidence-based coaching that treats you as a whole person — body,
            bank account, mindset and relationships. Small daily systems,
            lasting transformation.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.3 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-cta-ebooks"
              onClick={() => document.querySelector("#ebooks")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full bg-brand-text px-8 py-4 text-sm font-medium text-brand-bg hover:bg-brand-gold transition-colors duration-300"
            >
              Explore the ebooks
            </button>
            <button
              data-testid="hero-cta-contact"
              onClick={() => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full border border-brand-line px-8 py-4 text-sm font-medium text-brand-text hover:border-brand-gold hover:text-brand-gold transition-colors duration-300"
            >
              Start your journey
            </button>
          </motion.div>
        </div>

        <div className="lg:col-span-5">
          <motion.div
            style={{ y: yImg, scale: scaleImg }}
            initial={{ opacity: 0, scale: 1.06 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.4, ease: [0.16, 1, 0.3, 1] }}
            className="relative"
          >
            <div className="arch overflow-hidden border border-brand-line">
              <img
                src={IMAGES.portrait}
                alt="TonyBrand29"
                className="w-full h-[520px] lg:h-[620px] object-cover object-top"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 glass border border-brand-line rounded-2xl px-6 py-4">
              <p className="font-serif text-2xl text-brand-text">Whole‑person</p>
              <p className="overline mt-1">care & coaching</p>
            </div>
            {embed && (
              <button
                data-testid="hero-video-play"
                onClick={() => setShowVideo(true)}
                className="absolute top-4 right-4 z-20 flex items-center gap-2 glass border border-brand-line rounded-full pl-3 pr-4 py-2 text-brand-text hover:text-brand-gold transition-colors"
              >
                <span className="grid place-items-center w-8 h-8 rounded-full bg-brand-gold text-brand-bg">
                  <Play size={16} weight="fill" />
                </span>
                <span className="text-sm">Watch welcome</span>
              </button>
            )}
          </motion.div>
        </div>
      </div>

      <button
        onClick={scrollDown}
        data-testid="hero-scroll"
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-brand-dim hover:text-brand-gold transition-colors"
      >
        <span className="overline">Scroll</span>
        <ArrowDown size={18} className="animate-bounce" />
      </button>

      {showVideo && embed && (
        <div
          data-testid="hero-video-modal"
          className="fixed inset-0 z-[60] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8"
          onClick={() => setShowVideo(false)}
        >
          <button
            data-testid="hero-video-close"
            onClick={() => setShowVideo(false)}
            className="absolute top-5 right-5 z-10 grid place-items-center w-11 h-11 rounded-full glass border border-brand-line text-brand-text hover:text-brand-gold transition-colors"
          >
            <X size={22} />
          </button>
          <div
            className="w-full max-w-4xl aspect-video rounded-2xl overflow-hidden border border-brand-line bg-black"
            onClick={(e) => e.stopPropagation()}
          >
            {embed.type === "iframe" ? (
              <iframe
                src={embed.src}
                title="Welcome video"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <video src={embed.src} className="w-full h-full" controls autoPlay />
            )}
          </div>
        </div>
      )}
    </section>
  );
};
